/* =========================================================
   MAISON — Theme JavaScript
   ========================================================= */

'use strict';

// ── UTILITIES ──────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, wait = 200) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), wait); };
}

// ── HEADER SCROLL ─────────────────────────────────────────
function initHeaderScroll() {
  const header = $('#site-header');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 20);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ── SEARCH TOGGLE ──────────────────────────────────────────
function initSearch() {
  const toggle = $('.search-toggle');
  const bar = $('#search-bar');
  const close = $('#search-close');
  const input = bar ? bar.querySelector('.search-input') : null;

  if (!toggle || !bar) return;

  const open = () => {
    bar.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    setTimeout(() => input && input.focus(), 50);
  };

  const close_ = () => {
    bar.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  };

  toggle.addEventListener('click', () => {
    bar.classList.contains('is-open') ? close_() : open();
  });

  close && close.addEventListener('click', close_);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && bar.classList.contains('is-open')) close_();
  });
}

// ── MOBILE MENU ────────────────────────────────────────────
function initMobileMenu() {
  const toggle = $('.mobile-menu-toggle');
  const menu = $('#mobile-menu');
  const overlay = $('#mobile-menu-overlay');
  const close = $('#mobile-menu-close');

  if (!toggle || !menu) return;

  const open = () => {
    menu.classList.add('is-open');
    overlay && overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    toggle.setAttribute('aria-expanded', 'true');
  };

  const closeMenu = () => {
    menu.classList.remove('is-open');
    overlay && overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    toggle.setAttribute('aria-expanded', 'false');
  };

  toggle.addEventListener('click', open);
  close && close.addEventListener('click', closeMenu);
  overlay && overlay.addEventListener('click', closeMenu);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeMenu();
  });
}

// ── SCROLL REVEAL ──────────────────────────────────────────
function initScrollReveal() {
  const elements = $$('.product-card, .testimonial-card, .image-with-text__content, .image-with-text__media, .section-header');

  elements.forEach((el, i) => {
    el.classList.add('reveal');
    if (i % 4 === 1) el.classList.add('reveal--delay-1');
    if (i % 4 === 2) el.classList.add('reveal--delay-2');
    if (i % 4 === 3) el.classList.add('reveal--delay-3');
  });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  elements.forEach(el => observer.observe(el));
}

// ── PRODUCT GALLERY ────────────────────────────────────────
function initProductGallery() {
  const gallery = $('.product-gallery');
  if (!gallery) return;

  const mainImg = gallery.querySelector('.product-gallery__main-img');
  const thumbs = $$('.product-gallery__thumb', gallery);

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src;
      const alt = thumb.dataset.alt;

      if (mainImg && src) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = src;
          mainImg.alt = alt || '';
          mainImg.style.opacity = '1';
        }, 150);
      }

      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

// ── VARIANT SELECTOR ───────────────────────────────────────
function initVariantSelector() {
  const form = $('[data-product-form]');
  if (!form) return;

  const variantInput = form.querySelector('[name="id"]');
  const addBtn = form.querySelector('.add-to-cart-btn');
  const priceEl = form.querySelector('.product-price');

  function updateVariant() {
    const selected = {};
    $$('[data-option-name]', form).forEach(btn => {
      if (btn.classList.contains('is-active')) {
        selected[btn.dataset.optionName] = btn.dataset.optionValue;
      }
    });

    const variants = JSON.parse(form.dataset.variants || '[]');
    const match = variants.find(v => {
      return v.options.every((opt, i) => {
        const optionKey = `option${i + 1}`;
        return Object.values(selected)[i] === opt;
      });
    });

    if (match && variantInput) {
      variantInput.value = match.id;

      if (addBtn) {
        if (!match.available) {
          addBtn.disabled = true;
          addBtn.textContent = 'Sold Out';
        } else {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to Cart';
        }
      }

      if (priceEl && match.price) {
        const formatted = (match.price / 100).toLocaleString('en-US', {
          style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
        });
        priceEl.textContent = formatted;
      }
    }
  }

  $$('[data-option-name]', form).forEach(btn => {
    btn.addEventListener('click', () => {
      const name = btn.dataset.optionName;
      $$(`[data-option-name="${name}"]`, form).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      updateVariant();
    });
  });
}

// ── QUANTITY BUTTONS ───────────────────────────────────────
function initQuantity() {
  $$('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.product-form__quantity')?.querySelector('.qty-input');
      if (!input) return;
      const current = parseInt(input.value) || 1;
      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 99;

      if (btn.dataset.action === 'minus') {
        input.value = Math.max(min, current - 1);
      } else {
        input.value = Math.min(max, current + 1);
      }
    });
  });
}

// ── ADD TO CART ────────────────────────────────────────────
function initAddToCart() {
  const forms = $$('[data-product-form]');

  forms.forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = form.querySelector('.add-to-cart-btn');
      const originalText = btn ? btn.textContent : '';
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding…';
      }

      const formData = new FormData(form);
      const data = { items: [{ id: formData.get('id'), quantity: formData.get('quantity') || 1 }] };

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });

        if (!res.ok) throw new Error('Failed to add to cart');

        // Update cart count
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const countEl = $('.cart-count');
        if (countEl) {
          countEl.textContent = cart.item_count;
          countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }

        showCartNotification();

        if (btn) {
          btn.textContent = 'Added!';
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = originalText;
          }, 1800);
        }

      } catch (err) {
        console.error('Cart error:', err);
        if (btn) {
          btn.disabled = false;
          btn.textContent = originalText;
        }
      }
    });
  });
}

// Quick add buttons on product cards
function initQuickAdd() {
  $$('.product-card__quick-add[data-product-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.productId;
      if (!id) return;

      const original = btn.textContent;
      btn.textContent = '…';

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] })
        });

        btn.textContent = 'Added!';
        showCartNotification();

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const countEl = $('.cart-count');
        if (countEl) {
          countEl.textContent = cart.item_count;
          countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }

        setTimeout(() => { btn.textContent = original; }, 1800);
      } catch (e) {
        btn.textContent = original;
      }
    });
  });
}

// ── CART NOTIFICATION ──────────────────────────────────────
function showCartNotification() {
  let notif = $('.cart-notification');

  if (!notif) {
    notif = document.createElement('div');
    notif.className = 'cart-notification';
    notif.innerHTML = `
      <span class="cart-notification__text">Item added to your cart</span>
      <a href="/cart" class="cart-notification__link">View Cart</a>
    `;
    document.body.appendChild(notif);
  }

  notif.classList.add('is-visible');
  setTimeout(() => notif.classList.remove('is-visible'), 3000);
}

// ── ACCORDION ──────────────────────────────────────────────
function initAccordions() {
  $$('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');

      // Close siblings (optional — comment out for multi-open)
      const siblings = $$('.accordion-item', item.closest('.accordion'));
      siblings.forEach(s => s.classList.remove('is-open'));

      if (!isOpen) item.classList.add('is-open');
    });
  });
}

// ── COLLECTION FILTERS ─────────────────────────────────────
function initFilters() {
  const filterTags = $$('.filter-tag');
  filterTags.forEach(tag => {
    tag.addEventListener('click', () => {
      tag.classList.toggle('is-active');
    });
  });

  // Sort
  const sortSelect = $('.sort-select');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }
}

// ── PRODUCT GRID TABS ──────────────────────────────────────
function initProductTabs() {
  $$('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      $$('.tab-btn').forEach(b => b.classList.remove('tab-btn--active'));
      btn.classList.add('tab-btn--active');
      // Tab filtering could be implemented here with JS fetch
    });
  });
}

// ── INIT ───────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initHeaderScroll();
  initSearch();
  initMobileMenu();
  initScrollReveal();
  initProductGallery();
  initVariantSelector();
  initQuantity();
  initAddToCart();
  initQuickAdd();
  initAccordions();
  initFilters();
  initProductTabs();
});
